<script setup>
// NOTICE: KAPTION-INTEGRATION
// two-step-verification.UI.3 — every add/remove flow in one place, so the
// profile section and the AdminRequired screen behave the same:
//   add(method)           confirms it is the owner (fresh sign-in or password),
//                         opens that method's dialog; once it is added and the
//                         person has no backup codes yet, they are created and
//                         shown right away (canvas: BackupCodes)
//   regenerateBackupCodes asks for the password, then shows the new set
//   remove(method, key)   asks for the password, then removes it
// Emits `updated` with the fresh status after every change and `finished` once
// an add flow is completely over (backup codes dialog closed, if shown).
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KaptionTwoStepAPI from 'dashboard/api/kaptionTwoStep';
import KaptionWhatsapp2faAPI from 'dashboard/api/kaptionWhatsapp2fa';
import KaptionTwoStepPasskeyDialog from './KaptionTwoStepPasskeyDialog.vue';
import KaptionTwoStepAuthenticatorDialog from './KaptionTwoStepAuthenticatorDialog.vue';
import KaptionTwoStepWhatsappDialog from './KaptionTwoStepWhatsappDialog.vue';
import KaptionTwoStepBackupCodesDialog from './KaptionTwoStepBackupCodesDialog.vue';
import KaptionTwoStepPasswordDialog from './KaptionTwoStepPasswordDialog.vue';

const emit = defineEmits(['updated', 'finished']);
const { t } = useI18n();

const passkeyDialog = ref(null);
const authenticatorDialog = ref(null);
const whatsappDialog = ref(null);
const backupDialog = ref(null);
const passwordDialog = ref(null);

const methodTitle = method => {
  if (method === 'passkey') return t('KAPTION_TWO_STEP.METHODS.PASSKEY.TITLE');
  if (method === 'whatsapp')
    return t('KAPTION_TWO_STEP.METHODS.WHATSAPP.TITLE');
  return t('KAPTION_TWO_STEP.METHODS.AUTHENTICATOR.TITLE');
};

const addedMessage = method => {
  if (method === 'passkey')
    return t('KAPTION_TWO_STEP.BACKUP_DIALOG.ADDED.PASSKEY');
  if (method === 'whatsapp')
    return t('KAPTION_TWO_STEP.BACKUP_DIALOG.ADDED.WHATSAPP');
  return t('KAPTION_TWO_STEP.BACKUP_DIALOG.ADDED.AUTHENTICATOR');
};

const errorFor = e => {
  const errorCode = e?.response?.data?.error_code;
  if (errorCode === 'last_method_required')
    return t('KAPTION_TWO_STEP.REMOVE_DIALOG.LAST_METHOD');
  return e?.response?.data?.error || t('KAPTION_TWO_STEP.ERRORS.GENERIC');
};

const openMethodDialog = method => {
  if (method === 'passkey') passkeyDialog.value?.open();
  else if (method === 'authenticator') authenticatorDialog.value?.open();
  else if (method === 'whatsapp') whatsappDialog.value?.open();
};

// two-step-verification.API.3 — adding a method needs a fresh sign-in or the
// password. Ask for it only when the server says so.
const add = async method => {
  try {
    await KaptionTwoStepAPI.stepUp();
    openMethodDialog(method);
    return;
  } catch (e) {
    if (e?.response?.data?.error_code !== 'step_up_required') {
      useAlert(errorFor(e));
      return;
    }
  }
  passwordDialog.value?.open({
    title: t('KAPTION_TWO_STEP.STEP_UP.TITLE'),
    description: t('KAPTION_TWO_STEP.STEP_UP.DESC'),
    confirmLabel: t('KAPTION_TWO_STEP.STEP_UP.CONFIRM'),
    destructive: false,
    onConfirm: async password => {
      try {
        await KaptionTwoStepAPI.stepUp(password);
        openMethodDialog(method);
        return null;
      } catch (e) {
        return e?.response?.data?.error_code === 'invalid_credentials'
          ? t('KAPTION_TWO_STEP.STEP_UP.INVALID')
          : errorFor(e);
      }
    },
  });
};

const onAdded = async ({ method, status }) => {
  emit('updated', status);
  if (!status?.methods?.backup_codes?.pending) {
    useAlert(addedMessage(method));
    emit('finished');
    return;
  }
  try {
    const { data } = await KaptionTwoStepAPI.generateBackupCodes();
    emit('updated', data);
    backupDialog.value?.open({ backupCodes: data.backup_codes, method });
  } catch (e) {
    useAlert(errorFor(e));
  }
};

const generateFirstBackupCodes = async () => {
  try {
    const { data } = await KaptionTwoStepAPI.generateBackupCodes();
    emit('updated', data);
    backupDialog.value?.open({ backupCodes: data.backup_codes });
  } catch (e) {
    useAlert(errorFor(e));
  }
};

const regenerateBackupCodes = () => {
  passwordDialog.value?.open({
    title: t('KAPTION_TWO_STEP.BACKUP_DIALOG.REGEN_TITLE'),
    description: t('KAPTION_TWO_STEP.BACKUP_DIALOG.REGEN_DESC'),
    confirmLabel: t('KAPTION_TWO_STEP.BACKUP_DIALOG.REGEN_CONFIRM'),
    destructive: false,
    onConfirm: async password => {
      try {
        const { data } = await KaptionTwoStepAPI.generateBackupCodes(password);
        emit('updated', data);
        backupDialog.value?.open({ backupCodes: data.backup_codes });
        return null;
      } catch (e) {
        return errorFor(e);
      }
    },
  });
};

const removeRequest = (method, passkeyId, password) => {
  if (method === 'passkey')
    return KaptionTwoStepAPI.removePasskey(passkeyId, password);
  if (method === 'authenticator')
    return KaptionTwoStepAPI.removeAuthenticator(password);
  return KaptionWhatsapp2faAPI.disable(password);
};

const remove = (method, passkeyId = null) => {
  passwordDialog.value?.open({
    title: t('KAPTION_TWO_STEP.REMOVE_DIALOG.TITLE', {
      method: methodTitle(method),
    }),
    description: t('KAPTION_TWO_STEP.REMOVE_DIALOG.DESC'),
    confirmLabel: t('KAPTION_TWO_STEP.REMOVE_DIALOG.CONFIRM'),
    onConfirm: async password => {
      try {
        await removeRequest(method, passkeyId, password);
        const { data } = await KaptionTwoStepAPI.getStatus();
        emit('updated', data);
        return null;
      } catch (e) {
        return errorFor(e);
      }
    },
  });
};

defineExpose({ add, remove, regenerateBackupCodes, generateFirstBackupCodes });
</script>

<template>
  <KaptionTwoStepPasskeyDialog ref="passkeyDialog" @added="onAdded" />
  <KaptionTwoStepAuthenticatorDialog
    ref="authenticatorDialog"
    @added="onAdded"
  />
  <KaptionTwoStepWhatsappDialog ref="whatsappDialog" @added="onAdded" />
  <KaptionTwoStepBackupCodesDialog
    ref="backupDialog"
    @closed="emit('finished')"
  />
  <KaptionTwoStepPasswordDialog ref="passwordDialog" />
</template>
